
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  AlertTriangle, 
  CheckCircle, 
  Users, 
  RefreshCw, 
  ChevronDown, 
  ChevronRight,
  Wrench,
  Zap
} from 'lucide-react';
import { useDataConsistencyReport, useFixDataInconsistencies, useEnhancedConsistencyReport, useEnhancedAutoFix } from '@/hooks/useDataConsistency';
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useIsMobile } from '@/hooks/use-mobile';

export const DataConsistencyPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [useEnhanced, setUseEnhanced] = useState(true);
  const isMobile = useIsMobile();
  
  const { data: report, isLoading: isLoadingBasic, refetch: refetchBasic } = useDataConsistencyReport();
  const { data: enhancedReport, isLoading: isLoadingEnhanced, refetch: refetchEnhanced } = useEnhancedConsistencyReport();
  
  const fixInconsistencies = useFixDataInconsistencies();
  const enhancedAutoFix = useEnhancedAutoFix();

  const currentReport = useEnhanced ? enhancedReport : report;
  const isLoading = useEnhanced ? isLoadingEnhanced : isLoadingBasic;

  const handleRefresh = () => {
    if (useEnhanced) {
      refetchEnhanced();
    } else {
      refetchBasic();
    }
  };

  const handleFixIssues = () => {
    if (useEnhanced && enhancedReport) {
      enhancedAutoFix.mutate(enhancedReport);
    } else if (report?.issues && report.issues.length > 0) {
      fixInconsistencies.mutate(report.issues);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Data Consistency
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentReport) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Unable to load data consistency report.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const hasIssues = useEnhanced ? 
    (enhancedReport?.inconsistentUsers || 0) > 0 : 
    (report?.inconsistentUsers || 0) > 0;

  const issuesCount = useEnhanced ? 
    enhancedReport?.issues?.length || 0 : 
    report?.issues?.length || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Data Consistency
            {hasIssues && (
              <Badge variant="destructive" className="ml-2">
                {issuesCount} Issues
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUseEnhanced(!useEnhanced)}
              className="text-xs"
            >
              {useEnhanced ? 'Enhanced' : 'Basic'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className={`grid ${isMobile ? 'grid-cols-2 gap-3' : 'grid-cols-4 gap-4'} mb-4`}>
          <div className="text-center">
            <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-blue-600`}>
              {currentReport.totalUsers}
            </div>
            <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>Total Users</div>
          </div>
          <div className="text-center">
            <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-green-600`}>
              {useEnhanced ? enhancedReport?.consistentUsers : report?.assignedUsers}
            </div>
            <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
              {useEnhanced ? 'Consistent' : 'Assigned'}
            </div>
          </div>
          <div className="text-center">
            <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-gray-600`}>
              {useEnhanced ? 
                (enhancedReport?.totalUsers || 0) - (enhancedReport?.consistentUsers || 0) - (enhancedReport?.inconsistentUsers || 0) :
                report?.unassignedUsers
              }
            </div>
            <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>Unassigned</div>
          </div>
          <div className="text-center">
            <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold ${hasIssues ? 'text-red-600' : 'text-green-600'}`}>
              {currentReport.inconsistentUsers}
            </div>
            <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>Issues</div>
          </div>
        </div>

        {/* Enhanced Summary for Enhanced Mode */}
        {useEnhanced && enhancedReport?.summary && (
          <div className="mb-4 p-3 border rounded-lg bg-muted/50">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-red-600">Critical: {enhancedReport.summary.critical}</span>
              <span className="text-orange-600">High: {enhancedReport.summary.high}</span>
              <span className="text-yellow-600">Medium: {enhancedReport.summary.medium}</span>
              <span className="text-green-600">Auto-fixable: {enhancedReport.summary.autoFixable}</span>
            </div>
          </div>
        )}

        {/* Status indicator */}
        <div className="flex items-center gap-2 mb-4">
          {hasIssues ? (
            <>
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-red-600 font-medium">Data inconsistencies detected</span>
            </>
          ) : (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-green-600 font-medium">All data is consistent</span>
            </>
          )}
        </div>

        {/* Issues details */}
        {hasIssues && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button
                onClick={handleFixIssues}
                disabled={fixInconsistencies.isPending || enhancedAutoFix.isPending}
                size="sm"
                className="flex items-center gap-2"
              >
                {useEnhanced ? <Zap className="h-4 w-4" /> : <Wrench className="h-4 w-4" />}
                {fixInconsistencies.isPending || enhancedAutoFix.isPending ? 'Fixing...' : 
                 useEnhanced ? 'Enhanced Auto-Fix' : 'Auto-Fix Issues'}
              </Button>
            </div>

            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2" />
                  )}
                  {isExpanded ? 'Hide' : 'Show'} Issue Details ({issuesCount})
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                {isMobile ? (
                  // Mobile card layout
                  <div className="space-y-3">
                    {(useEnhanced ? enhancedReport?.issues : report?.issues)?.map((issue, index) => (
                      <Card key={index} className="p-3">
                        <div className="space-y-2">
                          <div className="font-medium text-sm">
                            {useEnhanced ? (issue as any).userName : issue.userName}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {useEnhanced ? (issue as any).userEmail : issue.userEmail}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {useEnhanced ? (issue as any).type : 'Legacy'}
                            </Badge>
                            {useEnhanced && (
                              <Badge className={`text-xs ${getSeverityColor((issue as any).severity)}`}>
                                {(issue as any).severity}
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {useEnhanced ? (issue as any).description : issue.issue}
                          </div>
                          <div className="text-xs bg-muted p-2 rounded">
                            {useEnhanced ? (issue as any).recommendedAction : issue.recommendedAction}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Desktop table layout
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">User</TableHead>
                          <TableHead className="text-xs">Issue</TableHead>
                          {useEnhanced && <TableHead className="text-xs">Severity</TableHead>}
                          <TableHead className="text-xs">Recommended Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(useEnhanced ? enhancedReport?.issues : report?.issues)?.map((issue, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <div>
                                <div className="font-medium text-sm">
                                  {useEnhanced ? (issue as any).userName : issue.userName}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {useEnhanced ? (issue as any).userEmail : issue.userEmail}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {useEnhanced ? (issue as any).description : issue.issue}
                              </Badge>
                            </TableCell>
                            {useEnhanced && (
                              <TableCell>
                                <Badge className={`text-xs ${getSeverityColor((issue as any).severity)}`}>
                                  {(issue as any).severity}
                                </Badge>
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="text-xs text-muted-foreground max-w-xs">
                                {useEnhanced ? (issue as any).recommendedAction : issue.recommendedAction}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
